import "@/App.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "sonner";
import api from "@/lib/api";
import { PopupProvider } from "@/components/AppPopups";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ACCESS, defaultPathForUser } from "@/lib/permissions";
import { Home } from "@/pages/Home";

import { Login } from "@/pages/Login";
import { ForgotPassword } from "@/pages/ForgotPassword";
import { SuperAdminDashboard } from "@/pages/SuperAdminDashboard";
import { AdvertiserDetail, AdvertiserFormPage, Advertisers } from "@/pages/Advertisers";
import { AdvertiserDashboard } from "@/pages/dashboard/AdvertiserDashboard";
import { Hoardings } from "@/pages/hoardings/Hoardings";
import { HoardingForm } from "@/pages/HoardingForm";
import { HoardingDetail } from "@/pages/hoardings/HoardingDetail";
import { PartnerHoardingDetail } from "@/pages/hoardings/PartnerHoardingDetail";
import { Reports } from "@/pages/Reports";
import { Analysis } from "@/pages/Analysis";
import { Clients, ClientDetail } from "@/pages/Clients";
import { Notifications } from "@/pages/Notifications";
import { Enquiries } from "@/pages/Enquiries";
import { Profile } from "@/pages/Profile";
import { Business } from "@/pages/Business";
import { Campaigns } from "@/pages/campaigns/Campaigns";
import { CampaignDetail } from "@/pages/campaigns/CampaignDetail";
import { CampaignEdit } from "@/pages/campaigns/CampaignEdit";
import { CreateCampaign } from "@/pages/campaigns/CreateCampaign";
import { CreateProposal } from "@/pages/CreateProposal";
import { ProposalDetail } from "@/pages/ProposalDetail";
import { SentProposals } from "@/pages/SentProposals";
import { ProposalArrangements } from "@/pages/ProposalArrangements";
import { PartnerNetwork } from "@/pages/PartnerNetwork";
import { FinanceOperations } from "@/pages/FinanceOperations";
import { Accounts } from "@/pages/Accounts";
import { AuditLogs } from "@/pages/AuditLogs";
import { CreateEmployeePage, Employees } from "@/pages/Employees";
import { DataMigration } from "@/pages/DataMigration";
import { PrivacyPolicy } from "@/pages/PrivacyPolicy";
import { PublicProposal } from "@/pages/PublicProposal";
import { Pricing } from "@/pages/Pricing";
import { Careers } from "@/pages/Careers";
import { PLAN_FEATURES } from "@/lib/planFeatures";

const ADVERTISER_ROLES = ["advertiser_admin", "employee"];

function dashboardPath(user) {
  return defaultPathForUser(user);
}

function RootRedirect() {
  const { user, checking } = useAuth();
  if (checking) return null;
  if (!user) return <Navigate to="/" replace />;
  return <Navigate to={dashboardPath(user)} replace />;
}

// Wraps the public Home page: if already logged in, bounce to the dashboard
function HomeRoute({ children }) {
  const { user, checking } = useAuth();
  if (checking) return null;
  if (user) return <Navigate to={dashboardPath(user)} replace />;
  return children;
}

function GuestRoute({ children }) {
  const { user, checking } = useAuth();
  if (checking) return null;
  if (user) return <Navigate to={dashboardPath(user)} replace />;
  return children;
}

function MigrationGate() {
  const { user } = useAuth();
  const [migration, setMigration] = useState(null);
  const pollTimerRef = useRef(null);
  const lastStatusCheckRef = useRef(0);
  const canCheckMigration = user?.role === "advertiser_admin" || user?.role === "employee";

  const clearPollTimer = useCallback(() => {
    if (pollTimerRef.current) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const loadMigrationStatus = useCallback((shouldContinuePolling = false) => {
    if (!canCheckMigration) {
      clearPollTimer();
      setMigration(null);
      return;
    }

    lastStatusCheckRef.current = Date.now();
    api.get("/data-migration/status", { skipGlobalLoader: true })
      .then(({ data }) => {
        const activeMigration = data?.active ? data : null;
        setMigration(activeMigration);
        clearPollTimer();

        if (activeMigration && shouldContinuePolling) {
          pollTimerRef.current = window.setTimeout(() => loadMigrationStatus(true), 5000);
        }
      })
      .catch(() => {
        clearPollTimer();
      });
  }, [canCheckMigration, clearPollTimer]);

  useEffect(() => {
    if (!canCheckMigration) {
      clearPollTimer();
      setMigration(null);
      return undefined;
    }

    loadMigrationStatus(true);
    return () => {
      clearPollTimer();
    };
  }, [canCheckMigration, clearPollTimer, loadMigrationStatus]);

  useEffect(() => {
    if (!canCheckMigration) return undefined;

    const onFocus = () => {
      if (Date.now() - lastStatusCheckRef.current > 60000) {
        loadMigrationStatus(true);
      }
    };

    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [canCheckMigration, loadMigrationStatus]);

  if (!migration) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/70 px-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-lg bg-white p-6 text-center shadow-2xl">
        <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-4 border-blue-100 border-t-red-600" />
        <h2 className="text-lg font-bold text-slate-950">Your data migration is in progress</h2>
        <p className="mt-2 text-sm text-slate-600">
          {migration.message || "Please wait while we import your records."}
        </p>
        <div className="mt-4 rounded-md bg-slate-50 px-3 py-2 text-xs font-semibold text-slate-600">
          {migration.phase || migration.status}
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <PopupProvider>
            <Toaster richColors position="top-right" />
            <MigrationGate />
            <Routes>
            <Route path="/" element={<HomeRoute><Home /></HomeRoute>} />
            <Route path="/app" element={<RootRedirect />} />
            <Route path="/login" element={<GuestRoute><Login /></GuestRoute>} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/proposal/:token" element={<PublicProposal />} />

            {/* Super Admin */}
            <Route path="/super-admin" element={
              <ProtectedRoute role="super_admin"><SuperAdminDashboard /></ProtectedRoute>
            }/>
            <Route path="/super-admin/advertisers" element={
              <ProtectedRoute role="super_admin"><Advertisers /></ProtectedRoute>
            }/>
            <Route path="/super-admin/advertisers/new" element={
              <ProtectedRoute role="super_admin"><AdvertiserFormPage mode="create" /></ProtectedRoute>
            }/>
            <Route path="/super-admin/advertisers/:id" element={
              <ProtectedRoute role="super_admin"><AdvertiserDetail /></ProtectedRoute>
            }/>
            <Route path="/super-admin/advertisers/:id/edit" element={
              <ProtectedRoute role="super_admin"><AdvertiserFormPage mode="edit" /></ProtectedRoute>
            }/>
            <Route path="/super-admin/notifications" element={
              <ProtectedRoute role="super_admin"><Notifications /></ProtectedRoute>
            }/>
            <Route path="/super-admin/enquiries" element={
              <ProtectedRoute role="super_admin"><Enquiries /></ProtectedRoute>
            }/>
            <Route path="/super-admin/data-migration" element={
              <ProtectedRoute role="super_admin"><DataMigration /></ProtectedRoute>
            }/>

            {/* Advertiser + Employee shared routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.dashboard}><AdvertiserDashboard /></ProtectedRoute>
            }/>
            <Route path="/hoardings" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.hoardings}><Hoardings /></ProtectedRoute>
            }/>
            <Route path="/hoardings/new" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={["create_hoardings"]}><HoardingForm /></ProtectedRoute>
            }/>
            <Route path="/hoardings/partner/:listingId" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.partnerNetwork}><PartnerHoardingDetail /></ProtectedRoute>
            }/>
            <Route path="/hoardings/:id" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.hoardings}><HoardingDetail /></ProtectedRoute>
            }/>
            <Route path="/hoardings/:id/edit" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={["edit_own_hoardings", "edit_assigned_hoardings", "edit_all_hoardings"]}><HoardingForm /></ProtectedRoute>
            }/>
            <Route path="/availability" element={<Navigate to="/hoardings?view=availability" replace />} />
            <Route path="/city-analytics" element={<Navigate to="/finance" replace />} />
            <Route path="/campaigns" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.campaigns}><Campaigns /></ProtectedRoute>
            }/>
            <Route path="/campaigns/new" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={["create_campaigns"]}><CreateCampaign /></ProtectedRoute>
            }/>
            <Route path="/campaigns/:id" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.campaigns}><CampaignDetail /></ProtectedRoute>
            }/>
            <Route path="/campaigns/:id/edit" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={["edit_own_campaigns", "edit_assigned_campaigns", "edit_all_campaigns", "modify_records"]}><CampaignEdit /></ProtectedRoute>
            }/>
            <Route path="/proposals" element={<Navigate to="/proposals/create" replace />} />
            <Route path="/proposals/create" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.proposals}><CreateProposal /></ProtectedRoute>
            }/>
            <Route path="/proposals/sent" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.proposals}><SentProposals /></ProtectedRoute>
            }/>
            <Route path="/proposals/arrangements" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.proposalArrangements} planFeature={PLAN_FEATURES.proposalCustom}><ProposalArrangements /></ProtectedRoute>
            }/>
            <Route path="/proposals/:id" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.proposals}><ProposalDetail /></ProtectedRoute>
            }/>
            <Route path="/partner-network" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.partnerNetwork}><PartnerNetwork /></ProtectedRoute>
            }/>
            <Route path="/analysis" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.analysis} planFeature={PLAN_FEATURES.analysis}><Analysis /></ProtectedRoute>
            }/>
            <Route path="/insights" element={<Navigate to="/finance" replace />} />
            <Route path="/finance" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.financeOps} planFeature={PLAN_FEATURES.financeQueue}><FinanceOperations /></ProtectedRoute>
            }/>
            <Route path="/accounts" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.accounts} planFeature={PLAN_FEATURES.accounts}><Accounts /></ProtectedRoute>
            }/>
            <Route path="/clients" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.clients}><Clients /></ProtectedRoute>
            }/>
            <Route path="/clients/:id" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.clients}><ClientDetail /></ProtectedRoute>
            }/>
            <Route path="/reports" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.reports} planFeature={PLAN_FEATURES.reports}><Reports /></ProtectedRoute>
            }/>
            <Route path="/business" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.business}><Business /></ProtectedRoute>
            }/>
            <Route path="/audit-logs" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.audit}><AuditLogs /></ProtectedRoute>
            }/>
            <Route path="/branches" element={<Navigate to="/business" replace />} />
            <Route path="/profile" element={
              <ProtectedRoute role={ADVERTISER_ROLES}><Profile /></ProtectedRoute>
            }/>
            <Route path="/notifications" element={
              <ProtectedRoute role={ADVERTISER_ROLES}><Notifications /></ProtectedRoute>
            }/>
            {/* Admin-only: employee management */}
            <Route path="/employees" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={ACCESS.employees} planFeature={PLAN_FEATURES.employees}><Employees /></ProtectedRoute>
            }/>
            <Route path="/employees/new" element={
              <ProtectedRoute role={ADVERTISER_ROLES} permissions={["invite_employees"]} planFeature={PLAN_FEATURES.employees}><CreateEmployeePage /></ProtectedRoute>
            }/>
            <Route path="/data-migration" element={
              <ProtectedRoute role="super_admin"><Navigate to="/super-admin/data-migration" replace /></ProtectedRoute>
            }/>

            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </PopupProvider>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
